import React, { useEffect, useState } from "react";
import { getImageUrl } from "helpers/Image.helper";
import { formatTimeAgo } from "helpers/Datetime.helper";
import { getFlagByCountryCode } from "helpers/String.helper";
import { fetchStudentProfile } from "api/agency/v2/Popup.api";
import { IUserStudent } from "api/interfaces/user.interface";
import { IDiary } from "api/interfaces/profile.interface";
import { usePopup } from "contexts/PopupContext";
import StudentDiaryDetailPopup from "./StudentDiaryDetailPopup";
import { addFavorite, deleteFavorite } from "api/agency/v2/Favorite.api";
import { useNavigate } from "react-router-dom";
import FullPageStudentInfoPopupContainer from "./FullPageStudentInfoPopup.container";

interface StudentProfilePopupProps {
    userStudentId: number;
    onClose?: () => void;
}

const StudentProfilePopup: React.FC<StudentProfilePopupProps> = ({ userStudentId, onClose }) => {
    const { showPopup } = usePopup();
    const navigate = useNavigate();
    const [userStudent, setUserStudent] = useState<IUserStudent | null>(null);
    const [diaryList, setDiaryList] = useState<IDiary[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [isFavoriteLoading, setIsFavoriteLoading] = useState(false);
    const [currentImageIndex, setCurrentImageIndex] = useState(0);

    // 나이 계산 함수
    const calculateAge = (birthday: string | null): number | null => {
        if (!birthday) return null;
        const today = new Date();
        const birthDate = new Date(birthday);
        let age = today.getFullYear() - birthDate.getFullYear();
        const monthDiff = today.getMonth() - birthDate.getMonth();
        if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
            age--;
        }
        return age;
    };

    // 스크롤 이벤트 핸들러 - 현재 이미지 인덱스 업데이트
    const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
        const container = e.currentTarget;
        const scrollLeft = container.scrollLeft;
        const containerWidth = container.clientWidth;
        const newIndex = Math.round(scrollLeft / containerWidth);
        setCurrentImageIndex(newIndex);
    };

    // 즐겨찾기 토글 함수
    const handleFavoriteToggle = async () => {
        if (!userStudent || isFavoriteLoading) return;

        try {
            setIsFavoriteLoading(true);

            if (userStudent.isFavorite) {
                // 즐겨찾기 삭제
                await deleteFavorite(userStudentId);
            } else {
                // 즐겨찾기 추가
                await addFavorite(userStudentId);
            }

            // 상태 업데이트
            setUserStudent(prev => prev ? { ...prev, isFavorite: !prev.isFavorite } : null);
        } catch (error) {
            console.error("즐겨찾기 토글 실패:", error);
        } finally {
            setIsFavoriteLoading(false);
        }
    };

    useEffect(() => {
        fetchStudentProfileData();
    }, [userStudentId]);

    const fetchStudentProfileData = async () => {
        try {
            setIsLoading(true);
            setError(null);
            const result = await fetchStudentProfile(userStudentId);
            setUserStudent(result.userStudent);
            setDiaryList(result.diaryList || []);
        } catch (error) {
            console.error("지망생 프로필 조회 실패:", error);
            setError("지망생 정보를 불러오는데 실패했습니다.");
        } finally {
            setIsLoading(false);
        }
    };

    const handleShowDetailInfo = () => {
        navigate(`/agency/student/${userStudentId}`);
        if (onClose) {
            // showPopup(
            //     <FullPageStudentInfoPopupContainer
            //         userStudentId={userStudentId} />);
            onClose();
        }
    }

    const handleClose = () => {
        if (onClose) {
            onClose();
        }
    };

    // 로딩 상태
    if (isLoading) {
        return (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 px-4">
                <div className="popup-content bg-white rounded-2xl shadow-xl max-w-2xl w-full max-h-[90vh] flex flex-col relative overflow-y-hidden">
                    <div className="animate-pulse">
                        {/* 닫기 버튼 스켈레톤 */}
                        <div className="absolute top-4 right-4 w-8 h-8 bg-gray-300 rounded-full z-10"></div>

                        {/* 스크롤 가능한 전체 콘텐츠 영역 스켈레톤 */}
                        <div className="flex-1 overflow-y-auto">
                            {/* 프로필 사진 + 추가사진 갤러리 영역 스켈레톤 - 스와이프 가능 */}
                            <div className="relative">
                                <div className="w-full aspect-square rounded-t-2xl overflow-hidden relative">
                                    {/* 이미지 갤러리 컨테이너 스켈레톤 */}
                                    <div className="flex w-full h-full overflow-x-auto snap-x snap-mandatory scrollbar-hide">
                                        {/* 프로필 사진 스켈레톤 (첫 번째 슬라이드) */}
                                        <div className="flex-shrink-0 w-full h-full snap-center relative bg-gray-200">
                                        </div>

                                        {/* 추가 사진 스켈레톤들 (나머지 슬라이드들) */}
                                        {[...Array(3)].map((_, index) => (
                                            <div key={index} className="flex-shrink-0 w-full h-full snap-center relative bg-gray-200">
                                            </div>
                                        ))}
                                    </div>

                                    {/* 하단 그라데이션 오버레이 스켈레톤 - 모든 사진에서 표시 */}
                                    <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-gray-300/70 via-gray-200/30 to-transparent"></div>

                                    {/* 프로필 정보 스켈레톤 - 모든 사진에서 표시 */}
                                    <div className="absolute bottom-4 left-4 right-4">
                                        <div className="flex justify-between items-end">
                                            {/* 좌측: 나이, 이름, 국적 스켈레톤 */}
                                            <div>
                                                <div className="h-4 bg-white/30 rounded w-8 mb-2"></div>
                                                <div className="flex items-center gap-2">
                                                    <div className="h-5 bg-white/30 rounded w-20"></div>
                                                    <div className="h-5 bg-white/30 rounded w-6"></div>
                                                </div>
                                            </div>

                                            {/* 우측: 키, 몸무게 스켈레톤 */}
                                            <div className="text-right">
                                                <div className="h-4 bg-white/30 rounded w-16"></div>
                                            </div>
                                        </div>
                                    </div>

                                    {/* 현재 이미지 번호 / 총 이미지 수 스켈레톤 */}
                                    <div className="absolute top-4 left-4 bg-gray-300/50 text-white px-2 py-1 rounded text-sm w-12 h-6"></div>
                                </div>
                            </div>

                            {/* 지망생 프로필 내용 스켈레톤 */}
                            <div className="p-6 pb-20">
                                {/* 즐겨찾기한 기획사 목록 스켈레톤 */}
                                <div className="mb-6">
                                    <div className="flex items-center gap-3">
                                        {/* 즐겨찾기 아이콘 스켈레톤 - 고정 */}
                                        <div className="flex-shrink-0 w-8 h-8 bg-gray-100 rounded-full"></div>

                                        {/* 기획사 로고 스켈레톤들 - 스크롤 가능 */}
                                        <div className="flex gap-3 overflow-x-auto pb-2 flex-1">
                                            {[...Array(3)].map((_, index) => (
                                                <div key={index} className="flex-shrink-0 w-8 h-8 bg-gray-200 rounded-lg"></div>
                                            ))}
                                        </div>
                                    </div>
                                </div>


                                {/* 다이어리 목록 스켈레톤 */}
                                <div className="mb-6">
                                    <div className="h-6 bg-gray-200 rounded w-32 mb-4"></div>
                                    <div className="space-y-1">
                                        {[...Array(3)].map((_, index) => (
                                            <div key={index} className="flex items-start justify-between p-2 bg-gray-50 rounded-lg">
                                                <div className="flex-1">
                                                    <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
                                                    <div className="flex items-center space-x-3">
                                                        <div className="flex items-center space-x-1">
                                                            <div className="w-3 h-3 bg-gray-200 rounded"></div>
                                                            <div className="h-3 bg-gray-200 rounded w-4"></div>
                                                        </div>
                                                        <div className="flex items-center space-x-1">
                                                            <div className="w-3 h-3 bg-gray-200 rounded"></div>
                                                            <div className="h-3 bg-gray-200 rounded w-4"></div>
                                                        </div>
                                                    </div>
                                                </div>
                                                <div className="ml-2 h-3 bg-gray-200 rounded w-12"></div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* 하단 버튼 영역 스켈레톤 - 팝업 하단에 고정 */}
                        <div className="bg-white border-t border-gray-200 p-4 rounded-b-2xl">
                            <div className="flex gap-3">
                                <div className="w-12 h-12 bg-gray-200 rounded-lg"></div>
                                <div className="flex-1 h-12 bg-gray-200 rounded-lg"></div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    // 에러 상태
    if (error || !userStudent) {
        return (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
                <div className="bg-white rounded-2xl shadow-xl max-w-2xl w-full mx-4 p-6">
                    <div className="text-center">
                        <div className="text-red-500 text-6xl mb-4">⚠️</div>
                        <h3 className="text-lg font-semibold text-gray-900 mb-2">
                            지망생 정보 로드 실패
                        </h3>
                        <p className="text-gray-600 mb-4">
                            {error || "지망생 정보를 불러올 수 없습니다."}
                        </p>
                        <button
                            onClick={handleClose}
                            className="px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors"
                        >
                            닫기
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 px-4">
            <div className="popup-content bg-white rounded-2xl shadow-xl max-w-2xl w-full max-h-[90vh] flex flex-col relative">
                {/* 닫기 버튼 - 팝업 내부 상단 우측에 고정 */}
                <button
                    onClick={handleClose}
                    className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-full transition-colors z-10"
                >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                </button>

                {/* 스크롤 가능한 콘텐츠 영역 */}
                <div className="flex-1 overflow-y-auto">
                    {/* 프로필 사진 + 추가사진 갤러리 영역 - 스와이프 가능 */}
                    <div className="relative">
                        <div className="w-full aspect-square rounded-t-2xl overflow-hidden relative">
                            {/* 이미지 갤러리 컨테이너 */}
                            <div
                                className="flex w-full h-full overflow-x-auto snap-x snap-mandatory scrollbar-hide"
                                onScroll={handleScroll}
                            >
                                {/* 프로필 사진 (첫 번째 슬라이드) */}
                                <div className="flex-shrink-0 w-full h-full snap-center relative">
                                    <img
                                        src={getImageUrl(userStudent.profileImage) || "/imgs/dummy/basic-img.png"}
                                        alt={userStudent.nickname || "지망생 프로필 배경"}
                                        className="w-full h-full object-cover"
                                        onError={(e) => {
                                            const target = e.target as HTMLImageElement;
                                            target.src = "/imgs/dummy/basic-img.png";
                                        }}
                                    />
                                </div>

                                {/* 추가 사진들 (나머지 슬라이드들) */}
                                {userStudent.userStudentImages && userStudent.userStudentImages.map((imageInfo, index) => (
                                    <div
                                        key={imageInfo.id || index}
                                        className="flex-shrink-0 w-full h-full snap-center relative"
                                    >
                                        <img
                                            src={getImageUrl(imageInfo.image)}
                                            alt={`추가 사진 ${index + 1}`}
                                            className="w-full h-full object-cover"
                                            onError={(e) => {
                                                const target = e.target as HTMLImageElement;
                                                target.src = "/imgs/dummy/basic-img.png";
                                            }}
                                        />
                                    </div>
                                ))}
                            </div>

                            {/* 하단 그라데이션 오버레이 - 모든 사진에서 표시 */}
                            <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-black/70 via-black/30 to-transparent"></div>

                            {/* 프로필 정보 - 모든 사진에서 표시 */}
                            <div className="absolute bottom-4 left-4 right-4 text-white">
                                <div className="flex justify-between items-end">
                                    {/* 좌측: 나이, 이름, 국적 */}
                                    <div>
                                        {userStudent.birthday && (
                                            <p className="text-sm text-white/90 mb-1">
                                                {calculateAge(userStudent.birthday.toString())}세
                                            </p>
                                        )}
                                        <div className="flex items-center gap-2">
                                            <h3 className="text-lg font-bold">
                                                {userStudent.nickname || userStudent.user?.username || "지망생"}
                                            </h3>
                                            {userStudent.countryCode && (
                                                <span className="text-lg">
                                                    {getFlagByCountryCode(userStudent.countryCode)}
                                                </span>
                                            )}
                                        </div>
                                    </div>

                                    {/* 우측: 키, 몸무게 */}
                                    <div className="text-right">
                                        <div className="flex items-center gap-2 text-sm text-white/90">
                                            {userStudent.height && (
                                                <span>{userStudent.height}cm</span>
                                            )}
                                            {userStudent.height && userStudent.weight && (
                                                <span>•</span>
                                            )}
                                            {userStudent.weight && (
                                                <span>{userStudent.weight}kg</span>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* 현재 이미지 번호 / 총 이미지 수 - 동적으로 업데이트 */}
                            <div className="absolute top-4 left-4 bg-black/50 text-white px-2 py-1 rounded text-sm">
                                {currentImageIndex + 1} / {(userStudent.userStudentImages?.length || 0) + 1}
                            </div>
                        </div>
                    </div>

                    {/* 지망생 프로필 내용 */}
                    <div className="p-6 pb-20">
                        {/* 즐겨찾기한 기획사 목록 */}
                        {userStudent.userStudentFavoriteList && userStudent.userStudentFavoriteList.length > 0 && (
                            <div className="mb-6">
                                <div className="flex items-center gap-3">
                                    {/* 즐겨찾기 아이콘 - 고정 */}
                                    <div className="flex-shrink-0 w-8 h-8 rounded-full border-2 border-gray-300 bg-gray-100 shadow-sm flex items-center justify-center">
                                        <img
                                            src="/imgs/v2/icon-bookmark-active.svg"
                                            alt="즐겨찾기"
                                            className="w-2 h-2"
                                        />
                                        <br />
                                        <span className="text-xs text-gray-500 font-medium">
                                            {userStudent.userStudentFavoriteList.length}
                                        </span>
                                    </div>

                                    {/* 기획사 로고들 - 스크롤 가능 */}
                                    <div className="flex gap-3 overflow-x-auto pb-2 scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100 flex-1">
                                        {userStudent.userStudentFavoriteList.map((favorite) => (
                                            favorite?.userAgency?.agency && (
                                                <div
                                                    key={favorite.userAgencyId}
                                                    className="flex-shrink-0 w-8 h-8 rounded-lg overflow-hidden border-2 border-gray-600 bg-white shadow-sm"
                                                    title={favorite.userAgency?.agency?.nameKo || favorite.userAgency?.agency?.nameEn || "기획사"}
                                                >
                                                    <img
                                                        src={getImageUrl(favorite.userAgency?.agency?.logoImage) || "/imgs/dummy/basic-img.png"}
                                                        alt={favorite.userAgency?.agency?.nameKo || favorite.userAgency?.agency?.nameEn || "기획사"}
                                                        className="w-full h-full object-contain"
                                                        onError={(e) => {
                                                            const target = e.target as HTMLImageElement;
                                                            target.src = "/imgs/dummy/basic-img.png";
                                                        }}
                                                    />
                                                </div>
                                            )
                                        ))}
                                    </div>
                                </div>
                            </div>
                        )}


                        {/* 다이어리 목록 */}
                        {diaryList && diaryList.length > 0 && (
                            <div className="mb-6">
                                <h3 className="text-lg font-semibold text-gray-900 mb-4 border-b border-gray-200 pb-2">
                                    최근등록된 다이어리
                                </h3>
                                <div className="space-y-1">
                                    {diaryList.map((diary) => (
                                        <div
                                            key={diary.id}
                                            className="flex items-start justify-between p-2 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors cursor-pointer"
                                            onClick={() => {
                                                // 다이어리 상세 팝업 표시
                                                showPopup(
                                                    <StudentDiaryDetailPopup
                                                        diaryId={diary.id.toString()}
                                                        onClose={() => {
                                                            // 팝업이 닫힐 때 다이어리 목록 새로고침
                                                            fetchStudentProfileData();
                                                        }}
                                                    />
                                                );
                                            }}
                                        >
                                            <div className="flex-1">
                                                <div
                                                    className="text-sm text-[#CF3688] font-medium mb-1"
                                                    style={{
                                                        wordBreak: 'break-all',
                                                        overflowWrap: 'anywhere',
                                                        whiteSpace: 'normal'
                                                    }}
                                                >
                                                    {diary.title}
                                                </div>
                                                <div className="flex items-center space-x-3">
                                                    <div className="flex items-center space-x-1">
                                                        <img src="/imgs/icons/icon-heart.svg" alt="like" className="w-3 h-3" />
                                                        <span className="text-xs text-gray-500 font-medium">
                                                            {diary.likeCount || 0}
                                                        </span>
                                                    </div>
                                                    <div className="flex items-center space-x-1">
                                                        <img src="/imgs/icons/icon-comment.svg" alt="comment" className="w-3 h-3" />
                                                        <span className="text-xs text-gray-500 font-medium">
                                                            {diary.commentCount || 0}
                                                        </span>
                                                    </div>
                                                    <div className="flex items-center space-x-1">
                                                        <img src="/imgs/icons/icon-bookmark.svg" alt="bookmark" className="w-3 h-3" />
                                                        <span className="text-xs text-gray-500 font-medium">
                                                            {diary.bookmarkCount || 0}
                                                        </span>
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="ml-2 text-xs text-gray-500">
                                                {formatTimeAgo(diary.createdAt)}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                {/* 하단 버튼 영역 - 팝업 하단에 고정 */}
                <div className="bg-white border-t border-gray-200 p-4 rounded-b-2xl">
                    <div className="flex gap-3">
                        {/* 즐겨찾기 버튼 */}
                        <button
                            className={`flex items-center justify-center w-12 h-12 rounded-lg transition-colors ${isFavoriteLoading
                                ? 'cursor-not-allowed opacity-50'
                                : 'bg-gray-50 hover:bg-gray-100'
                                }`}
                            onClick={handleFavoriteToggle}
                            disabled={isFavoriteLoading}
                        >
                            {isFavoriteLoading ? (
                                // 로딩 중일 때 스피너 표시
                                <svg className="w-5 h-5 text-gray-400 animate-spin" fill="none" viewBox="0 0 24 24">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                </svg>
                            ) : (
                                // 즐겨찾기 아이콘
                                <img
                                    src={userStudent.isFavorite ? "/imgs/v2/icon-bookmark-active.svg" : "/imgs/v2/icon-bookmark.svg"}
                                    alt="즐겨찾기"
                                    className="w-5 h-5"
                                />
                            )}
                        </button>

                        {/* 상세정보 보기 버튼 */}
                        <button
                            className="flex-1 flex items-center justify-center gap-2 py-3 px-4 bg-[#CF3688] hover:bg-[#B82F77] text-white rounded-lg transition-colors font-medium"
                            onClick={handleShowDetailInfo}>
                            상세정보 보기
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default StudentProfilePopup;    